import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { db } from "../firebase";
import { serverTimestamp, deleteDoc, deleteField } from "firebase/firestore";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { getUserProfile } from "../utils/userProfile";

interface Post {
  id?: string;
  title: string;
  content: string;
  category?: string;
  tags?: string[];
  publishedAt: string;
  updatedAt: string;
  commentCount: number;
  viewCount?: number;
  savedBy?: string[];
  userId: string;
  imageUrl?: string; // Original image (backward compatibility)
  imageUrlMobile?: string; // 480px version for mobile (viewport ≤ 768px)
  imageUrlCard?: string; // 320px version for cards (viewport > 768px)
  videoUrl?: string; // YouTube, Vimeo, or other video embed URL
  isFeaturedProject?: boolean;
}

interface NewPostPayload {
  title: string;
  content: string;
  category: string;
  tags?: string[];
  userId: string;
  imageUrl?: string;
  imageUrlMobile?: string;
  imageUrlCard?: string;
  videoUrl?: string;
  isFeaturedProject?: boolean;
}

interface UpdatePostPayload {
  id: string;
  title: string;
  content: string;
  category: string;
  tags?: string[];
  userId?: string;
  imageUrl?: string;
  imageUrlMobile?: string;
  imageUrlCard?: string;
  videoUrl?: string;
  isFeaturedProject?: boolean;
}

interface InitialState {
  posts: Post[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: InitialState = {
  posts: [],
  status: "idle",
  error: null,
};

export const fetchPosts = createAsyncThunk("posts/fetchPosts", async () => {
  const querySnapshot = await getDocs(collection(db, "posts"));
  let posts: Post[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    posts.push({
      id: doc.id,
      title: data.title,
      content: data.content,
      category: data.category,
      tags: data.tags || [],
      publishedAt: data.publishedAt
        ? data.publishedAt.toDate().toISOString()
        : "N/A",
      updatedAt: data.updatedAt ? data.updatedAt.toDate().toISOString() : "N/A",
      commentCount: data.commentCount || 0,
      viewCount: data.viewCount || 0,
      userId: data.userId,
      imageUrl: data.imageUrl,
      imageUrlMobile: data.imageUrlMobile,
      imageUrlCard: data.imageUrlCard,
      videoUrl: data.videoUrl,
      isFeaturedProject: data.isFeaturedProject || false,
    } as Post);
  });
  return posts;
});

export const addPost = createAsyncThunk(
  "posts/addPost",
  async (post: NewPostPayload) => {
    const newPost: any = {
      ...post,
      commentCount: 0,
      publishedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isFeaturedProject: post.isFeaturedProject || false,
    };
    if (post.imageUrl) {
      newPost.imageUrl = post.imageUrl;
    }
    if (post.imageUrlMobile) {
      newPost.imageUrlMobile = post.imageUrlMobile;
    }
    if (post.imageUrlCard) {
      newPost.imageUrlCard = post.imageUrlCard;
    }
    const docRef = await addDoc(collection(db, "posts"), newPost);

    // Notify followers about the new post
    try {
      const authorProfile = await getUserProfile(post.userId);
      if (authorProfile) {
        const followers = (authorProfile as any).followers || [];
        const authorName = authorProfile.displayName || "Someone";

        // Send notification to each follower
        for (const followerUid of followers) {
          await addDoc(collection(db, "notifications"), {
            recipientUid: followerUid,
            fromUid: post.userId,
            fromName: authorName,
            postId: docRef.id,
            postTitle: post.title,
            type: "new_post",
            read: false,
            createdAt: serverTimestamp(),
          });
        }
      }
    } catch (notifError) {
      console.error("Error notifying followers:", notifError);
    }

    // Send newsletter to all subscribers
    try {
      // Call serverless function to send newsletter (fire and forget)
      fetch('/api/send-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ postId: docRef.id }),
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            console.error("❌ Newsletter API error:", response.status, errorText);
          } else {
            const result = await response.json();
            console.log("✅ Newsletter sent:", result);
          }
        })
        .catch((error) => {
          console.error("❌ Error sending newsletter:", error);
          // Don't throw - newsletter sending failure shouldn't prevent post creation
        });
    } catch (newsletterError) {
      console.error("❌ Error initiating newsletter send:", newsletterError);
      // Don't throw - newsletter sending failure shouldn't prevent post creation
    }

    return {
      id: docRef.id,
      ...post,
      publishedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as Post;
  }
);

export const updatePost = createAsyncThunk(
  "posts/updatePost",
  async (payload: UpdatePostPayload) => {
    const { id, title, content, category, tags, imageUrl, imageUrlMobile, imageUrlCard, videoUrl, isFeaturedProject } = payload;

    const postRef = doc(db, "posts", id);
    const updateData: any = {
      title,
      content,
      category,
      updatedAt: serverTimestamp(),
    };

    // Handle tags
    if (tags !== undefined) {
      updateData.tags = tags;
    }

    // Handle image fields - use deleteField() to remove them if undefined, otherwise set the value
    if (imageUrl === undefined) {
      updateData.imageUrl = deleteField();
    } else if (imageUrl !== null) {
      updateData.imageUrl = imageUrl;
    }

    if (imageUrlMobile === undefined) {
      updateData.imageUrlMobile = deleteField();
    } else if (imageUrlMobile !== null) {
      updateData.imageUrlMobile = imageUrlMobile;
    }

    if (imageUrlCard === undefined) {
      updateData.imageUrlCard = deleteField();
    } else if (imageUrlCard !== null) {
      updateData.imageUrlCard = imageUrlCard;
    }

    // Handle video URL
    if (videoUrl === undefined) {
      updateData.videoUrl = deleteField();
    } else if (videoUrl !== null) {
      updateData.videoUrl = videoUrl;
    }

    if (isFeaturedProject !== undefined) {
      updateData.isFeaturedProject = isFeaturedProject;
    }
    await updateDoc(postRef, updateData);
    return {
      ...payload,
      updatedAt: new Date().toISOString(),
    };
  }
);

export const getPost = createAsyncThunk("posts/get", async (postId: string) => {
  const postDoc = await getDoc(doc(db, "posts", postId));
  if (postDoc.exists()) {
    const data = postDoc.data();
    return {
      tags: data.tags || [],
      id: postDoc.id,
      ...data,
      userId: data.userId,
      publishedAt: data.publishedAt
        ? data.publishedAt.toDate().toISOString()
        : "N/A",
      updatedAt: data.updatedAt ? data.updatedAt.toDate().toISOString() : "N/A",
      commentCount: data.commentCount || 0,
      viewCount: data.viewCount || 0,
      imageUrl: data.imageUrl,
      imageUrlMobile: data.imageUrlMobile,
      imageUrlCard: data.imageUrlCard,
      isFeaturedProject: data.isFeaturedProject || false,
    } as Post;
  } else {
    throw new Error("Post not found");
  }
});

export const deletePost = createAsyncThunk(
  "posts/deletePost",
  async (postId: string) => {
    await deleteDoc(doc(db, "posts", postId));
    return postId;
  }
);

const postSlice = createSlice({
  name: "posts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.posts = action.payload;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(addPost.fulfilled, (state, action) => {
        const existingPostIndex = state.posts.findIndex(
          (post) => post.id === action.payload.id
        );
        if (existingPostIndex >= 0) {
          return;
        }
        state.posts.push(action.payload);
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        const { id, title, content, category, tags, imageUrl, imageUrlMobile, imageUrlCard, isFeaturedProject } = action.payload;
        const existingPost = state.posts.find((post) => post.id === id);
        if (existingPost) {
          existingPost.title = title;
          existingPost.content = content;
          existingPost.category = category;
          if (tags !== undefined) {
            existingPost.tags = tags;
          }
          existingPost.updatedAt = new Date().toISOString();
          // Handle image fields - explicitly set to undefined/null to clear them
          if (imageUrl === undefined || imageUrl === null) {
            delete existingPost.imageUrl;
          } else {
            existingPost.imageUrl = imageUrl;
          }
          if (imageUrlMobile === undefined || imageUrlMobile === null) {
            delete existingPost.imageUrlMobile;
          } else {
            existingPost.imageUrlMobile = imageUrlMobile;
          }
          if (imageUrlCard === undefined || imageUrlCard === null) {
            delete existingPost.imageUrlCard;
          } else {
            existingPost.imageUrlCard = imageUrlCard;
          }
          if (isFeaturedProject !== undefined) {
            existingPost.isFeaturedProject = isFeaturedProject;
          }
        }
      })
      .addCase(getPost.fulfilled, (state, action: PayloadAction<Post>) => {
        const index = state.posts.findIndex(
          (post) => post.id === action.payload.id
        );
        if (index !== -1) {
          state.posts[index] = action.payload;
        } else {
          state.posts.push(action.payload);
        }
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.posts = state.posts.filter((post) => post.id !== action.payload);
      });
  },
});

export default postSlice.reducer;
